import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { inviteClientToPlan } from '@services/planInvites';
import { buildClientInviteShareMessage, buildClientInviteShareUrl } from '@utils/clientInviteShare';
import InviteSharePanel from '@components/plan/InviteSharePanel';
import ShareVisibilitySettings from '@components/plan/ShareVisibilitySettings';
import {
	DEFAULT_SHARE_VISIBILITY,
	QUOTE_SHARE_VISIBILITY,
	type ShareVisibility
} from '@utils/shareVisibility';

type CopyFn = (en: string, de: string, hr: string) => string;

export type ShareClientLinkModalProps = {
	open: boolean;
	onClose: () => void;
	planDatabaseId: number;
	companyUserId: number;
	copy: CopyFn;
	/** When set, link opens the offers view for this quote after login */
	offerId?: number;
	/** Quote-share flow: no “allow client to edit plan” option */
	forQuoteShare?: boolean;
	existingInviteCode?: string | null;
	existingInviteEmail?: string | null;
	title?: string;
	introText?: string;
};

export default function ShareClientLinkModal({
	open,
	onClose,
	planDatabaseId,
	companyUserId,
	copy,
	offerId,
	forQuoteShare = false,
	existingInviteCode = null,
	existingInviteEmail = null,
	title,
	introText
}: ShareClientLinkModalProps) {
	const [inviteEmail, setInviteEmail] = useState('');
	const [inviteCode, setInviteCode] = useState<string | null>(existingInviteCode);
	const [shareText, setShareText] = useState<string | null>(null);
	const [shareVisibility, setShareVisibility] = useState<ShareVisibility>(
		forQuoteShare ? QUOTE_SHARE_VISIBILITY : DEFAULT_SHARE_VISIBILITY
	);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		setInviteEmail(existingInviteEmail ?? '');
		setInviteCode(existingInviteCode);
		setShareVisibility(forQuoteShare ? QUOTE_SHARE_VISIBILITY : DEFAULT_SHARE_VISIBILITY);
		if (existingInviteCode) {
			const url = buildClientInviteShareUrl(existingInviteCode, offerId);
			setShareText(buildClientInviteShareMessage(url, copy, { forQuote: forQuoteShare }));
		} else {
			setShareText(null);
		}
		setError(null);
	}, [open, existingInviteCode, existingInviteEmail, offerId, forQuoteShare, copy]);

	useEffect(() => {
		if (!open) return;
		document.body.classList.add('modal-open');
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
		};
	}, [open]);

	if (!open) return null;

	const modalTitle =
		title ??
		(forQuoteShare
			? copy('Share quote with client', 'Angebot mit Kunde teilen', 'Podijeli ponudu s klijentom')
			: copy('Invite client', 'Kunden einladen', 'Pozovi klijenta'));

	const modalIntro =
		introText ??
		(forQuoteShare
			? copy(
					'Your quote is saved. Share this link so your client can sign in, view the quote, and accept, decline, or message you. Linking a client is required before they can respond.',
					'Ihr Angebot ist gespeichert. Teilen Sie diesen Link, damit Ihr Kunde sich anmelden, das Angebot prüfen und annehmen, ablehnen oder Ihnen schreiben kann. Der Kunde muss verknüpft sein, bevor er antworten kann.',
					'Vaša je ponuda spremljena. Podijelite ovu poveznicu kako bi se klijent prijavio, pregledao ponudu te prihvatio, odbio ili vam poslao poruku. Klijent mora biti povezan prije nego što može odgovoriti.'
				)
			: copy(
					'Create a shareable link for this plan. If you add an email and they already have an account, the plan appears in their My Plans.',
					'Erstellen Sie einen teilbaren Link für diesen Plan. Mit E-Mail erscheint der Plan bei bestehenden Konten unter „Meine Pläne“.',
					'Kreirajte poveznicu za dijeljenje plana. Ako dodate e-mail, plan će se pojaviti u Mojim planovima za postojeće račune.'
				));

	const inviteReady = Boolean(inviteCode && shareText);

	const createOrRefreshInvite = async () => {
		setBusy(true);
		setError(null);
		try {
			const res = await inviteClientToPlan({
				planId: planDatabaseId,
				userId: companyUserId,
				invitedEmail: inviteEmail.trim() || undefined,
				allowEdit: forQuoteShare ? false : true,
				shareVisibility
			});
			const code = res.code ?? null;
			setInviteCode(code);
			if (code) {
				const url = buildClientInviteShareUrl(code, offerId);
				setShareText(buildClientInviteShareMessage(url, copy, { forQuote: forQuoteShare }));
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : copy('Could not create invite.', 'Einladung konnte nicht erstellt werden.', 'Pozivnica nije mogla biti kreirana.'));
		} finally {
			setBusy(false);
		}
	};

	return createPortal(
		<>
			<div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ zIndex: 1070 }}>
				<div className="modal-dialog modal-dialog-centered">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title">{modalTitle}</h5>
							<button
								type="button"
								className="btn-close"
								onClick={() => !busy && onClose()}
								aria-label={copy('Close', 'Schließen', 'Zatvori')}
							/>
						</div>
						<div className="modal-body">
							<p className="text-muted small mb-3">{modalIntro}</p>
							<div className="mb-2">
								<label className="form-label mb-1">{copy('Client email (optional)', 'Kunden-E-Mail (optional)', 'E-mail klijenta (neobavezno)')}</label>
								<input
									type="email"
									className="form-control"
									placeholder="client@email.com"
									value={inviteEmail}
									onChange={(e) => setInviteEmail(e.target.value)}
									disabled={busy || inviteReady}
								/>
							</div>
							<ShareVisibilitySettings
								value={shareVisibility}
								onChange={setShareVisibility}
								copy={copy}
								disabled={busy || inviteReady}
								compact
							/>
							{error && (
								<div className="alert alert-warning py-2 small mb-2" role="alert">
									{error}
								</div>
							)}
							{inviteReady && inviteCode && shareText && (
								<InviteSharePanel inviteCode={inviteCode} shareText={shareText} copy={copy} />
							)}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className={`btn ${inviteReady ? 'btn-success' : 'btn-outline-secondary'}`}
								onClick={() => !busy && onClose()}
								disabled={busy}
							>
								{copy('Close', 'Schließen', 'Zatvori')}
							</button>
							{!inviteReady && (
								<button type="button" className="btn btn-success" disabled={busy} onClick={() => void createOrRefreshInvite()}>
									{busy ? (
										<>
											<span className="spinner-border spinner-border-sm me-2" aria-hidden />
											{copy('Creating…', 'Wird erstellt…', 'Kreiranje…')}
										</>
									) : (
										copy('Create invite link', 'Einladungslink erstellen', 'Kreiraj poveznicu pozivnice')
									)}
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
			<div className="modal-backdrop fade show" style={{ zIndex: 1065 }} />
		</>,
		document.body
	);
}
